import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import type { Grant } from '@generated/form-types/grant';
import {
  EMAIL_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
  NAME_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
} from '@/constants/contact';
import { stripControlCharsInObject } from '@/xml/sanitize';
import { serializeXml } from '../xml-helpers';
import { formatTimestamp } from '../builders';
import {
  appendGrantElement,
  createGrantIdDocument,
} from './xml-helpers';
import { buildGrant } from './builders';

/**
 * Grant deposit XML. Document/head conventions (Grant ID 0.2.0 namespace,
 * `batch_000001`, timestamp, depositor from credentials, registrant).
 */
export class GrantXmlRenderingStrategy implements XMLGenerationStrategy {
  async generateXML(
    _recordType: ContentRegistrationType,
    data: FormData,
    prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const grantData = stripControlCharsInObject(data) as unknown as Grant;
    const meta = grantData.grantMetadata;
    if (!meta) {
      throw new Error('Grant form data is missing grantMetadata');
    }

    const doc = createGrantIdDocument();
    const root = doc.documentElement;

    buildGrantHead(root, userInfo);

    const body = appendGrantElement(root, 'body');
    buildGrant(
      body,
      meta,
      origin?.resourceFieldsImported?.relationships === true
    );

    return serializeXml(doc, prettyPrint);
  }
}

function buildGrantHead(root: Element, userInfo?: UserInfo): void {
  const head = appendGrantElement(root, 'head');
  appendGrantElement(head, 'doi_batch_id', 'batch_000001');
  appendGrantElement(head, 'timestamp', formatTimestamp(new Date()));

  const depositorEl = appendGrantElement(head, 'depositor');
  appendGrantElement(
    depositorEl,
    'depositor_name',
    userInfo?.depositorName || NAME_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN
  );
  appendGrantElement(
    depositorEl,
    'email_address',
    userInfo?.depositorEmail || EMAIL_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN
  );

  appendGrantElement(head, 'registrant', 'content-registration-form');
}
